import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useUpdateDealDetailsMutation } from '@crm/entities/deal';
import { formatPhoneInput } from '@crm/shared/lib';

import { handleInformationBlockError } from './handle-information-block-error';
import {
    createEditableFieldValidationSchemas,
    type DateFieldCommitHandler,
    type FieldEditHandler,
    type FieldErrors,
    type FieldFocusHandler,
    type FieldInputHandler,
    type FieldValue,
    formatSumDisplayValue,
    formatSumFocusValue,
    formatSumInputValue,
    getCurrentSubmitValue,
    getSubmitValue,
    isSumField,
    type UpdateFieldHandler,
    type UpdateFieldKeyDownHandler,
} from './informationBlockFields';

import type { TDealDetails, TUpdateDealDetailsFieldName } from '@crm/entities/deal';

export const useInformationBlockEditing = (
    dealData: TDealDetails['dealData'] | undefined
) => {
    const { t } = useTranslation();
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const lastSubmittedFieldValuesRef = useRef<
        Partial<Record<TUpdateDealDetailsFieldName, string | number | null>>
    >({});
    const submissionSequenceRef = useRef(0);
    const latestSubmissionIdsRef = useRef<Partial<Record<TUpdateDealDetailsFieldName, number>>>(
        {}
    );
    const updateDealDetailsMutation = useUpdateDealDetailsMutation();
    const editableFieldValidationSchemas = createEditableFieldValidationSchemas(t);

    useEffect(() => {
        lastSubmittedFieldValuesRef.current = {};
        latestSubmissionIdsRef.current = {};

        return () => {
            lastSubmittedFieldValuesRef.current = {};
            latestSubmissionIdsRef.current = {};
        };
    }, [dealData?.id]);

    const clearFieldError = (fieldName: TUpdateDealDetailsFieldName) => {
        setFieldErrors((currentErrors) => {
            if (!currentErrors[fieldName]) {
                return currentErrors;
            }

            const nextErrors = { ...currentErrors };
            delete nextErrors[fieldName];

            return nextErrors;
        });
    };

    const handleFieldEdit: FieldEditHandler = (fieldName) => {
        submissionSequenceRef.current += 1;
        latestSubmissionIdsRef.current[fieldName] = submissionSequenceRef.current;
        clearFieldError(fieldName);
    };

    const validateEditableField = (fieldName: TUpdateDealDetailsFieldName, value: string) => {
        const schema = editableFieldValidationSchemas.get(fieldName);

        if (!schema) {
            clearFieldError(fieldName);
            return true;
        }

        const validationResult = schema.safeParse(value);

        if (validationResult.success) {
            clearFieldError(fieldName);
            return true;
        }

        setFieldErrors((currentErrors) => ({
            ...currentErrors,
            [fieldName]:
                validationResult.error.issues[0]?.message ??
                t('crm.dealDetailsContent.informationBlock.validation.defaultError'),
        }));

        return false;
    };

    const updateField = (
        fieldName: TUpdateDealDetailsFieldName,
        initialValue: FieldValue,
        nextValue: string
    ) => {
        const submitValue = getSubmitValue(fieldName, nextValue);
        const currentValue = getCurrentSubmitValue(dealData, fieldName, initialValue);

        if (
            !dealData?.id ||
            submitValue === undefined ||
            submitValue === currentValue ||
            submitValue === lastSubmittedFieldValuesRef.current[fieldName]
        ) {
            return;
        }

        submissionSequenceRef.current += 1;
        const submissionId = submissionSequenceRef.current;

        lastSubmittedFieldValuesRef.current[fieldName] = submitValue;
        latestSubmissionIdsRef.current[fieldName] = submissionId;

        void updateDealDetailsMutation
            .mutateAsync({
                dealId: dealData.id,
                payload: {
                    [fieldName]: submitValue,
                },
            })
            .catch((error) => {
                if (lastSubmittedFieldValuesRef.current[fieldName] === submitValue) {
                    delete lastSubmittedFieldValuesRef.current[fieldName];
                }

                if (latestSubmissionIdsRef.current[fieldName] !== submissionId) {
                    return;
                }

                handleInformationBlockError({
                    error,
                    fallback: t('crm.common.deal.errors.update'),
                    setFieldErrors,
                });
            });
    };

    const handleFieldBlur: UpdateFieldHandler = (fieldName, initialValue, event) => {
        if (event.currentTarget.dataset.skipNextBlurSave === 'true') {
            delete event.currentTarget.dataset.skipNextBlurSave;
            return;
        }

        if (!validateEditableField(fieldName, event.currentTarget.value)) {
            return;
        }

        updateField(fieldName, initialValue, event.currentTarget.value);

        if (isSumField(fieldName)) {
            event.currentTarget.value = formatSumDisplayValue(event.currentTarget.value);
        }
    };

    const handleFieldFocus: FieldFocusHandler = (fieldName, event) => {
        if (isSumField(fieldName)) {
            event.currentTarget.value = formatSumFocusValue(
                event.currentTarget.dataset.rawValue ?? event.currentTarget.value
            );
        }
    };

    const handleFieldInput: FieldInputHandler = (fieldName, event) => {
        if (fieldName === 'phone') {
            event.currentTarget.value = formatPhoneInput(event.currentTarget.value);
        }

        handleFieldEdit(fieldName);

        if (isSumField(fieldName)) {
            event.currentTarget.value = formatSumInputValue(event.currentTarget.value);
        }
    };

    const handleFieldKeyDown: UpdateFieldKeyDownHandler = (fieldName, initialValue, event) => {
        if (event.key === 'Enter') {
            event.preventDefault();

            if (!validateEditableField(fieldName, event.currentTarget.value)) {
                return;
            }

            updateField(fieldName, initialValue, event.currentTarget.value);
            if (isSumField(fieldName)) {
                event.currentTarget.value = formatSumDisplayValue(event.currentTarget.value);
            }
            event.currentTarget.dataset.skipNextBlurSave = 'true';
            event.currentTarget.blur();
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            event.currentTarget.value = isSumField(fieldName)
                ? formatSumDisplayValue(initialValue)
                : String(initialValue ?? '');

            handleFieldEdit(fieldName);

            event.currentTarget.dataset.skipNextBlurSave = 'true';
            event.currentTarget.dataset.suppressHover = 'true';
            event.currentTarget.blur();
        }
    };

    const handleDateFieldCommit: DateFieldCommitHandler = (fieldName, initialValue, nextValue) => {
        if (!validateEditableField(fieldName, nextValue)) {
            return;
        }

        updateField(fieldName, initialValue, nextValue);
    };

    return {
        fieldErrors,
        handleDateFieldCommit,
        handleFieldEdit,
        handleFieldBlur,
        handleFieldFocus,
        handleFieldInput,
        handleFieldKeyDown,
    };
};
